import type { WeaponSlot } from "./schemas";

/** Industrial payload families — not fantasy magic. */
export type WeaponModElement = "thermal" | "cryo" | "corrosive" | "ion" | "shock";

export type WeaponModId =
  | "thermal_core"
  | "cryo_baffle"
  | "corrosive_tips"
  | "ion_shunt"
  | "shock_coil"
  | "kill_burst"
  | "scatter_latch"
  | "focus_latch"
  | "penetrator_sleeve"
  | "rebound_lens"
  | "blast_jacket"
  | "duplex_rack"
  | "mirror_coupler"
  | "impact_mill"
  | "crossfeed_relay"
  | "crown_array"
  | "capacitor_tap"
  | "recall_spool"
  | "counterpulse_emitter";

export type WeaponModKind = "payload" | "on_kill" | "pattern";

/** Per-stack curve values resolved at runtime from stacks. */
export type WeaponModStackCurve = {
  /** Burn DoT per second per stack. */
  burnDpsPerStack?: number;
  burnDurationS?: number;
  /** Fractional slow per stack (0.12 = 12%). Cap applied in sim. */
  slowFactorPerStack?: number;
  slowDurationS?: number;
  /** Extra hull damage fraction per stack after shields. */
  hullShredPerStack?: number;
  shredDurationS?: number;
  /** Bonus damage multiplier vs shields per stack. */
  shieldDamageBonusPerStack?: number;
  /** Fraction of remaining shield stripped on hit per stack. */
  shieldShredPerStack?: number;
  ionDurationS?: number;
  /** Seconds of telegraph interrupt / fire delay per stack. */
  shockDurationS?: number;
  /** Outward pellets spawned on kill per stack. */
  shrapnelCountPerStack?: number;
  /** Fraction of killing blow damage per shrapnel pellet. */
  shrapnelDamageFrac?: number;
  /** Pattern: delta to projectile count per stack. */
  countDeltaPerStack?: number;
  /** Pattern: delta to spread radians per stack. */
  spreadDeltaPerStack?: number;
  /** Pattern: fire-rate multiplier cumulative (e.g. 1.08 = slower CD). */
  cooldownMultPerStack?: number;
  /** Pattern: damage multiplier cumulative. */
  damageMultPerStack?: number;
  /** Pattern: additional targets passed through per stack. */
  pierceCountPerStack?: number;
  /** Fraction of current damage retained after each pierced target. */
  pierceDamageRetention?: number;
  /** Directed rebounds added to each projectile per stack. */
  ricochetCountPerStack?: number;
  ricochetRangeM?: number;
  ricochetDamageRetention?: number;
  /** Planar blast radius added to ordnance per stack. */
  blastRadiusMPerStack?: number;
  blastDamageRetention?: number;
  /** Radial fragments emitted by the first direct impact. */
  impactFragmentCountPerStack?: number;
  impactFragmentDamageRetention?: number;
  impactFragmentSpeed?: number;
  /** Fraction of other equipped weapon cooldowns removed on a kill. */
  crossSlotCooldownReductionPerStack?: number;
  /** Fraction of enemy-shield damage returned to the player's shield. */
  shieldLeechFractionPerStack?: number;
  /** Adds a tethered return leg after this many seconds of flight. */
  projectileReturnAfterS?: number;
  projectileReturnDamageRetention?: number;
  /** Ordnance blasts erase hostile projectiles inside their resolved radius. */
  counterpulseProjectileClear?: boolean;
  /** Soft-seek turn rate (rad/s) once homing unlocked; scales with stacks above unlock. */
  homingTurnRatePerStack?: number;
};

export type WeaponModDef = {
  id: WeaponModId;
  name: string;
  /** Short grit-appropriate description for level-up cards. */
  description: string;
  kind: WeaponModKind;
  element: WeaponModElement | null;
  maxStacks: number;
  /** Career rank before this upgrade can enter level-up offers. */
  minPilotRank?: number;
  /** Permanent blueprint required before this mod can enter run offers. */
  requiresBlueprint?: string;
  /** Preferred slot when offering; "any" rolls against equipped weapons. */
  preferredSlot: WeaponSlot | "any";
  /** Stacks of this mod (or slot elemental total) needed for soft homing. */
  homingUnlockStacks: number;
  /** Soft-seek lock distance in meters. */
  homingRangeM: number;
  /** Hex tint for projectiles when this payload is active. */
  tint: number;
  /** Compact HUD chip (3–4 chars). */
  chip: string;
  curve: WeaponModStackCurve;
};

/**
 * Weapon-focused level mods. Stacks amplify; high stacks unlock soft homing.
 * Homing unlock breakpoint: 3 stacks (see HOMING_UNLOCK_STACKS).
 */
export const HOMING_UNLOCK_STACKS = 3;
export const HOMING_RANGE_M = 28;
export const MAX_SLOW_FACTOR = 0.55;
export const MAX_HULL_SHRED = 0.75;

export const WEAPON_MODS: Record<WeaponModId, WeaponModDef> = {
  thermal_core: {
    id: "thermal_core",
    name: "Thermal Core",
    description: "Hotter tracers; burn DoT stacks hotter on hull.",
    kind: "payload",
    element: "thermal",
    maxStacks: 5,
    preferredSlot: "any",
    homingUnlockStacks: HOMING_UNLOCK_STACKS,
    homingRangeM: HOMING_RANGE_M,
    tint: 0xff6a3a,
    chip: "THRM",
    curve: {
      burnDpsPerStack: 4.5,
      burnDurationS: 2.2,
      homingTurnRatePerStack: 1.8,
    },
  },
  cryo_baffle: {
    id: "cryo_baffle",
    name: "Cryo Baffle",
    description: "Cold payload; slows hostiles on hit. Stacks last longer.",
    kind: "payload",
    element: "cryo",
    maxStacks: 5,
    preferredSlot: "any",
    homingUnlockStacks: HOMING_UNLOCK_STACKS,
    homingRangeM: HOMING_RANGE_M,
    tint: 0x7ec8ff,
    chip: "CRYO",
    curve: {
      slowFactorPerStack: 0.12,
      slowDurationS: 1.6,
      homingTurnRatePerStack: 1.6,
    },
  },
  corrosive_tips: {
    id: "corrosive_tips",
    name: "Corrosive Tips",
    description: "Hull shred — armor softens; more damage through plating.",
    kind: "payload",
    element: "corrosive",
    maxStacks: 5,
    preferredSlot: "any",
    homingUnlockStacks: HOMING_UNLOCK_STACKS,
    homingRangeM: HOMING_RANGE_M,
    tint: 0x8bc34a,
    chip: "CORR",
    curve: {
      hullShredPerStack: 0.1,
      shredDurationS: 3.5,
      homingTurnRatePerStack: 1.5,
    },
  },
  ion_shunt: {
    id: "ion_shunt",
    name: "Ion Shunt",
    description: "Shields weakened — bonus vs capacitors, shred remaining shield.",
    kind: "payload",
    element: "ion",
    maxStacks: 5,
    preferredSlot: "any",
    homingUnlockStacks: HOMING_UNLOCK_STACKS,
    homingRangeM: HOMING_RANGE_M,
    tint: 0xb48cff,
    chip: "ION",
    curve: {
      shieldDamageBonusPerStack: 0.22,
      shieldShredPerStack: 0.08,
      ionDurationS: 2.8,
      homingTurnRatePerStack: 1.7,
    },
  },
  shock_coil: {
    id: "shock_coil",
    name: "Shock Coil",
    description: "Brief stagger — interrupts aim telegraphs and fire windows.",
    kind: "payload",
    element: "shock",
    maxStacks: 4,
    preferredSlot: "any",
    homingUnlockStacks: HOMING_UNLOCK_STACKS,
    homingRangeM: HOMING_RANGE_M,
    tint: 0xffe066,
    chip: "SHOK",
    curve: {
      shockDurationS: 0.35,
      homingTurnRatePerStack: 1.4,
    },
  },
  kill_burst: {
    id: "kill_burst",
    name: "Kill Burst",
    description: "On kill: shrapnel sprays outward. Stacks add pellets.",
    kind: "on_kill",
    element: null,
    maxStacks: 4,
    preferredSlot: "any",
    homingUnlockStacks: 99,
    homingRangeM: 0,
    tint: 0xffb060,
    chip: "BRST",
    curve: {
      shrapnelCountPerStack: 3,
      shrapnelDamageFrac: 0.28,
    },
  },
  scatter_latch: {
    id: "scatter_latch",
    name: "Scatter Latch",
    description: "Spread pattern — more pellets, wider cone, slower cycle.",
    kind: "pattern",
    element: null,
    maxStacks: 3,
    preferredSlot: "primary",
    homingUnlockStacks: 99,
    homingRangeM: 0,
    tint: 0xe8c878,
    chip: "SCAT",
    curve: {
      countDeltaPerStack: 1,
      spreadDeltaPerStack: 0.06,
      cooldownMultPerStack: 1.08,
      damageMultPerStack: 0.94,
    },
  },
  focus_latch: {
    id: "focus_latch",
    name: "Focus Latch",
    description: "Focus pattern — tighter cone, harder hits, faster cycle.",
    kind: "pattern",
    element: null,
    maxStacks: 3,
    preferredSlot: "primary",
    homingUnlockStacks: 99,
    homingRangeM: 0,
    tint: 0xf0e0b0,
    chip: "FOCUS",
    curve: {
      countDeltaPerStack: -1,
      spreadDeltaPerStack: -0.05,
      cooldownMultPerStack: 0.9,
      damageMultPerStack: 1.12,
    },
  },
  penetrator_sleeve: {
    id: "penetrator_sleeve",
    name: "Penetrator Sleeve",
    description: "Rounds pass through one extra target per stack; each pass sheds power.",
    kind: "pattern",
    element: null,
    maxStacks: 3,
    minPilotRank: 21,
    preferredSlot: "any",
    homingUnlockStacks: 99,
    homingRangeM: 0,
    tint: 0xf4d88a,
    chip: "PEN",
    curve: {
      pierceCountPerStack: 1,
      pierceDamageRetention: 0.72,
      damageMultPerStack: 0.94,
    },
  },
  rebound_lens: {
    id: "rebound_lens",
    name: "Rebound Lens",
    description: "Secondary shots rebound to one extra nearby target per stack.",
    kind: "pattern",
    element: null,
    maxStacks: 2,
    minPilotRank: 34,
    preferredSlot: "secondary",
    homingUnlockStacks: 99,
    homingRangeM: 0,
    tint: 0x72f0d0,
    chip: "BNCE",
    curve: {
      ricochetCountPerStack: 1,
      ricochetRangeM: 20,
      ricochetDamageRetention: 0.74,
      damageMultPerStack: 0.93,
    },
  },
  blast_jacket: {
    id: "blast_jacket",
    name: "Blast Jacket",
    description: "Ordnance gains a wider planar blast; payload yield trades direct damage.",
    kind: "pattern",
    element: null,
    maxStacks: 2,
    minPilotRank: 44,
    requiresBlueprint: "blast_jacket_line",
    preferredSlot: "ordnance",
    homingUnlockStacks: 99,
    homingRangeM: 0,
    tint: 0xff8a4a,
    chip: "BLST",
    curve: {
      blastRadiusMPerStack: 4,
      blastDamageRetention: 0.45,
      damageMultPerStack: 0.92,
    },
  },
  duplex_rack: {
    id: "duplex_rack",
    name: "Duplex Rack",
    description: "Ordnance launches a split pair; each warhead yields less and cycles slower.",
    kind: "pattern",
    element: null,
    maxStacks: 1,
    minPilotRank: 50,
    requiresBlueprint: "duplex_rack_line",
    preferredSlot: "ordnance",
    homingUnlockStacks: 99,
    homingRangeM: 0,
    tint: 0xffb060,
    chip: "DPLX",
    curve: {
      countDeltaPerStack: 1,
      spreadDeltaPerStack: 0.12,
      cooldownMultPerStack: 1.15,
      damageMultPerStack: 0.72,
    },
  },
  mirror_coupler: {
    id: "mirror_coupler",
    name: "Mirror Coupler",
    description:
      "Secondary weapons gain a mirrored extra shot; each bolt yields less and resets slower.",
    kind: "pattern",
    element: null,
    maxStacks: 1,
    minPilotRank: 58,
    requiresBlueprint: "mirror_coupler_line",
    preferredSlot: "secondary",
    homingUnlockStacks: 99,
    homingRangeM: 0,
    tint: 0x8ad4ff,
    chip: "MIRR",
    curve: {
      countDeltaPerStack: 1,
      spreadDeltaPerStack: 0.1,
      cooldownMultPerStack: 1.12,
      damageMultPerStack: 0.72,
    },
  },
  impact_mill: {
    id: "impact_mill",
    name: "Impact Mill",
    description:
      "Primary impacts eject four radial shards; direct yield drops and the breech resets slower.",
    kind: "pattern",
    element: null,
    maxStacks: 1,
    minPilotRank: 64,
    requiresBlueprint: "impact_mill_line",
    preferredSlot: "primary",
    homingUnlockStacks: 99,
    homingRangeM: 0,
    tint: 0xffc36a,
    chip: "MILL",
    curve: {
      impactFragmentCountPerStack: 4,
      impactFragmentDamageRetention: 0.2,
      impactFragmentSpeed: 76,
      cooldownMultPerStack: 1.14,
      damageMultPerStack: 0.7,
    },
  },
  crossfeed_relay: {
    id: "crossfeed_relay",
    name: "Crossfeed Relay",
    description: "Kills with this slot advance every other equipped weapon cycle by 30%.",
    kind: "on_kill",
    element: null,
    maxStacks: 1,
    minPilotRank: 74,
    requiresBlueprint: "crossfeed_relay_line",
    preferredSlot: "any",
    homingUnlockStacks: 99,
    homingRangeM: 0,
    tint: 0xffdc7a,
    chip: "XFD",
    curve: {
      crossSlotCooldownReductionPerStack: 0.3,
    },
  },
  crown_array: {
    id: "crown_array",
    name: "Crown Array",
    description:
      "Any fitted weapon gains another diverging shot per stack; each projectile yields less and the cycle resets slower.",
    kind: "pattern",
    element: null,
    maxStacks: 2,
    minPilotRank: 82,
    requiresBlueprint: "crown_array_line",
    preferredSlot: "any",
    homingUnlockStacks: 99,
    homingRangeM: 0,
    tint: 0xb69cff,
    chip: "CRWN",
    curve: {
      countDeltaPerStack: 1,
      spreadDeltaPerStack: 0.08,
      cooldownMultPerStack: 1.1,
      damageMultPerStack: 0.76,
    },
  },
  capacitor_tap: {
    id: "capacitor_tap",
    name: "Capacitor Tap",
    description:
      "Damage absorbed by enemy shields returns 8% to your shield per stack; the tapped slot cycles slower.",
    kind: "pattern",
    element: null,
    maxStacks: 2,
    minPilotRank: 92,
    requiresBlueprint: "capacitor_tap_line",
    preferredSlot: "any",
    homingUnlockStacks: 99,
    homingRangeM: 0,
    tint: 0x6ff0cf,
    chip: "TAP",
    curve: {
      shieldLeechFractionPerStack: 0.08,
      cooldownMultPerStack: 1.06,
    },
  },
  recall_spool: {
    id: "recall_spool",
    name: "Recall Spool",
    description:
      "Secondary projectiles reverse after 0.55s and retain 82% damage on recall; the fitted slot cycles slower.",
    kind: "pattern",
    element: null,
    maxStacks: 1,
    minPilotRank: 98,
    requiresBlueprint: "recall_spool_line",
    preferredSlot: "secondary",
    homingUnlockStacks: 99,
    homingRangeM: 0,
    tint: 0xb69cff,
    chip: "RCL",
    curve: {
      projectileReturnAfterS: 0.55,
      projectileReturnDamageRetention: 0.82,
      cooldownMultPerStack: 1.18,
    },
  },
  counterpulse_emitter: {
    id: "counterpulse_emitter",
    name: "Centurion Counterpulse",
    description:
      "Ordnance blasts erase hostile rounds inside their radius; direct yield drops and the emitter cycles slower.",
    kind: "pattern",
    element: null,
    maxStacks: 1,
    minPilotRank: 100,
    requiresBlueprint: "counterpulse_emitter_line",
    preferredSlot: "ordnance",
    homingUnlockStacks: 99,
    homingRangeM: 0,
    tint: 0xffd27a,
    chip: "CNTR",
    curve: {
      counterpulseProjectileClear: true,
      damageMultPerStack: 0.88,
      cooldownMultPerStack: 1.12,
    },
  },
};

export const WEAPON_MOD_LIST: WeaponModDef[] = Object.values(WEAPON_MODS);

export function weaponModById(id: string): WeaponModDef | undefined {
  return WEAPON_MODS[id as WeaponModId];
}

export function elementTint(element: WeaponModElement | null | undefined): number | null {
  if (!element) return null;
  const match = WEAPON_MOD_LIST.find((m) => m.element === element);
  return match?.tint ?? null;
}

/** Offer token stored in skillPickOffers: `wmod:<modId>:<slot>`. */
export function encodeWeaponModOffer(modId: WeaponModId, slot: WeaponSlot): string {
  return `wmod:${modId}:${slot}`;
}

export function isWeaponModOffer(id: string): boolean {
  return id.startsWith("wmod:");
}

export function parseWeaponModOffer(id: string): { modId: WeaponModId; slot: WeaponSlot } | null {
  if (!isWeaponModOffer(id)) return null;
  const parts = id.split(":");
  if (parts.length !== 3) return null;
  const modId = parts[1] as WeaponModId;
  const slot = parts[2] as WeaponSlot;
  if (!WEAPON_MODS[modId]) return null;
  if (slot !== "primary" && slot !== "secondary" && slot !== "ordnance") return null;
  return { modId, slot };
}
